import React, { useState, useEffect } from 'react';
import { ArrowLeft, Target, LayoutList, MessageCircle, MoreVertical, Trash2 } from 'lucide-react';
import { ABLE_COLORS } from '../../constants';
import FilterPanel from '../FilterPanel';
import StateCards from './StateCards';
import FocusModeCard from './Tasks/FocusModeCard';
import PlanModeList from './Tasks/PlanModeList';
import { useToast } from '../Toast';

/**
 * TasksView - Focus/Plan modes inside Hub container
 *
 * Matches Hub's visual style (600px, cards).
 * Toggle between Focus (one task) and Plan (task list) modes.
 */
export default function TasksView({
  tasks,
  filteredTasks,
  currentTask,
  focusIndex,
  onBack,
  onOpenChat,
  onAddTask,
  onMarkDone,
  onMarkUndone,
  onSkip,
  onUpdateTask,
  onDeleteTask,
  onToggleStar,
  onClearAllTasks,
  // State check-in props
  currentState,
  onStateChange,
  // Filter props
  capacity,
  setCapacity,
  statusFilter,
  setStatusFilter,
  showStarredOnly,
  setShowStarredOnly,
  expandedFilter,
  setExpandedFilter,
  contextOptions
}) {
  const { showToast } = useToast();
  const [mode, setMode] = useState('focus'); // 'focus' | 'plan'
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showMenu, setShowMenu] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div>
      {/* Header with back button, mode toggle, and chat */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
        gap: '8px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            marginLeft: '-8px',
            color: ABLE_COLORS.textSecondary,
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          <ArrowLeft size={20} />
          Back
        </button>

        {/* Time display - hidden on very narrow screens */}
        <div style={{
          fontSize: '14px',
          fontWeight: '600',
          color: ABLE_COLORS.textMuted,
          display: 'none'
        }}
        className="time-display"
        >
          {formatTime(currentTime)}
        </div>
        <style>{`
          @media (min-width: 400px) {
            .time-display { display: block !important; }
          }
        `}</style>

        {/* Mode toggle */}
        <div style={{
          display: 'flex',
          background: ABLE_COLORS.bgSubtle,
          borderRadius: '10px',
          padding: '4px'
        }}>
          <button
            onClick={() => setMode('focus')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              background: mode === 'focus' ? 'white' : 'transparent',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              color: mode === 'focus' ? ABLE_COLORS.primary : ABLE_COLORS.textMuted,
              fontSize: '13px',
              fontWeight: '600',
              boxShadow: mode === 'focus' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.15s ease'
            }}
          >
            <Target size={16} />
            Focus
          </button>
          <button
            onClick={() => setMode('plan')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              background: mode === 'plan' ? 'white' : 'transparent',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              color: mode === 'plan' ? ABLE_COLORS.primary : ABLE_COLORS.textMuted,
              fontSize: '13px',
              fontWeight: '600',
              boxShadow: mode === 'plan' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.15s ease'
            }}
          >
            <LayoutList size={16} />
            Plan
          </button>
        </div>

        {/* Chat button */}
        <button
          onClick={onOpenChat}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            background: ABLE_COLORS.neutral,
            border: `1px solid ${ABLE_COLORS.border}`,
            borderRadius: '10px',
            cursor: 'pointer',
            color: ABLE_COLORS.warm,
            transition: 'all 0.15s ease'
          }}
          title="Chat with Mabel"
        >
          <MessageCircle size={20} />
        </button>

        {/* More menu */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              background: showMenu ? ABLE_COLORS.bgSubtle : 'transparent',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              color: ABLE_COLORS.textMuted,
              transition: 'all 0.15s ease'
            }}
            title="More options"
          >
            <MoreVertical size={20} />
          </button>

          {/* Dropdown menu */}
          {showMenu && (
            <>
              {/* Backdrop to close menu */}
              <div
                onClick={() => setShowMenu(false)}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 99
                }}
              />
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '4px',
                background: ABLE_COLORS.neutral,
                borderRadius: ABLE_COLORS.radiusMedium,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                border: `1px solid ${ABLE_COLORS.border}`,
                minWidth: '180px',
                zIndex: 100,
                overflow: 'hidden'
              }}>
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setShowClearConfirm(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    color: ABLE_COLORS.semantic.error,
                    fontSize: '14px',
                    fontWeight: '500',
                    textAlign: 'left',
                    transition: 'background 0.15s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = ABLE_COLORS.bgSubtle}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <Trash2 size={16} />
                  Clear all tasks
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Clear confirmation modal */}
      {showClearConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: ABLE_COLORS.neutral,
            borderRadius: ABLE_COLORS.cardRadius,
            padding: '24px',
            maxWidth: '320px',
            width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: ABLE_COLORS.textPrimary
            }}>
              Clear all tasks?
            </h3>
            <p style={{
              margin: '0 0 20px 0',
              fontSize: '14px',
              color: ABLE_COLORS.textSecondary,
              lineHeight: '1.5'
            }}>
              This will permanently delete all {tasks.length} tasks. This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowClearConfirm(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: ABLE_COLORS.bgSubtle,
                  border: 'none',
                  borderRadius: ABLE_COLORS.buttonRadius,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: ABLE_COLORS.textSecondary
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onClearAllTasks();
                  setShowClearConfirm(false);
                  showToast('All tasks cleared');
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: ABLE_COLORS.semantic.error,
                  border: 'none',
                  borderRadius: ABLE_COLORS.buttonRadius,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'white'
                }}
              >
                Clear all
              </button>
            </div>
          </div>
        </div>
      )}

      {/* State Cards */}
      <StateCards
        currentState={currentState}
        onStateChange={onStateChange}
        onToast={showToast}
      />

      {/* Filter Panel */}
      <FilterPanel
        capacity={capacity}
        setCapacity={setCapacity}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        showStarredOnly={showStarredOnly}
        setShowStarredOnly={setShowStarredOnly}
        expandedFilter={expandedFilter}
        setExpandedFilter={setExpandedFilter}
        contextOptions={contextOptions}
        viewMode={mode}
      />

      {/* Focus Mode */}
      {mode === 'focus' && (
        <FocusModeCard
          currentTask={currentTask}
          filteredTasks={filteredTasks}
          focusIndex={focusIndex}
          onMarkDone={onMarkDone}
          onSkip={onSkip}
          onUpdateTask={onUpdateTask}
        />
      )}

      {/* Plan Mode */}
      {mode === 'plan' && (
        <PlanModeList
          tasks={tasks}
          filteredTasks={filteredTasks}
          onAddTask={onAddTask}
          onMarkDone={onMarkDone}
          onMarkUndone={onMarkUndone}
          onToggleStar={onToggleStar}
          onUpdateTask={onUpdateTask}
          onDeleteTask={onDeleteTask}
          contextOptions={contextOptions}
        />
      )}
    </div>
  );
}
